// The macOS "genie" minimize effect.
//
// The window is copied into thin horizontal strips. Each frame, every strip is mapped onto the
// trapezoid that its rows occupy along a funnel running from the window down to its Dock icon:
// first the window's lower part pinches in toward the icon (the "squeeze"), then the whole window
// slides down the funnel into it (the "slide"). Restoring plays it backwards.
//
// Smoothness:
// - Strips are bent with a projective transform (matrix3d), which the GPU applies without
//   repainting anything, and which makes neighboring strips meet exactly along the curve.
// - Building the strips (copying and laying out the window) is the slow part, so it can be done
//   ahead of time with prepareGenie(), e.g. while the pointer is over the minimize button.

const STRIP = 18; // px of window height per strip...
const MAX_STRIPS = 40; // ...or more in a tall window, so the copy stays light
const SQUEEZE_END = 0.55; // the squeeze runs over the first 55% of the animation...
const SLIDE_START = 0.22; // ...and the slide over the last 78%, overlapping it
const PREPARED_TTL = 10000; // a prepared copy unused for this long is thrown away

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const smoothstep = (t) => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const easeIn = (t) => t * t * t;

export const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// The Dock icon an app minimizes into.
export const dockIcon = (app) =>
  document.querySelector(`.dock-button[aria-label="${app}"] img, .dock-button[aria-label="${app}"] .dock-clock`);

// Removes from `copy` everything that starts below `limit` (a screen y) in `original`, which can
// never show inside the window. A long scrolled resume is mostly below the window, so this keeps
// each strip's copy small. Only content after the cut goes, so nothing kept moves.
const pruneBelow = (original, copy, limit) => {
  const src = original.children;
  const dst = copy.children;
  for (let i = src.length - 1; i >= 0; i--) {
    const r = src[i].getBoundingClientRect();
    if (!r.width && !r.height) continue;
    // Look inside everything that starts on screen: a scrolling area's own box ends at the window
    // edge even when its content runs far below it.
    if (r.top > limit) dst[i].remove();
    else if (src[i].children.length) pruneBelow(src[i], dst[i], limit);
  }
};

// CSS transform mapping a w x h element onto the quadrilateral tl, tr, br, bl (screen points).
const quadTransform = (w, h, [x0, y0], [x1, y1], [x2, y2], [x3, y3]) => {
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const dy3 = y0 - y1 + y2 - y3;
  let g = 0;
  let k = 0;
  const det = dx1 * dy2 - dx2 * dy1;
  if ((Math.abs(dx3) > 1e-6 || Math.abs(dy3) > 1e-6) && Math.abs(det) > 1e-9) {
    g = (dx3 * dy2 - dx2 * dy3) / det;
    k = (dx1 * dy3 - dx3 * dy1) / det;
  }
  const a = x1 - x0 + g * x1;
  const b = x3 - x0 + k * x3;
  const d = y1 - y0 + g * y1;
  const e = y3 - y0 + k * y3;
  return `matrix3d(${a / w},${d / w},0,${g / w},${b / h},${e / h},0,${k / h},0,0,1,0,${x0},${y0},0,1)`;
};

// Changes that make a prepared copy out of date: where the window is, and how it's scrolled.
const snapshotKey = (source) => {
  const r = source.getBoundingClientRect();
  const scrolls = [...source.querySelectorAll("*")]
    .filter((el) => el.scrollTop || el.scrollLeft)
    .map((el) => `${el.scrollTop}:${el.scrollLeft}`)
    .join(",");
  return `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)},${Math.round(r.height)}|${scrolls}`;
};

// One prepared copy per window, reused if nothing has changed since it was made.
const prepared = new WeakMap();

const discard = (source) => {
  const p = prepared.get(source);
  if (!p) return;
  prepared.delete(source);
  p.observers.forEach((o) => o.disconnect());
  clearTimeout(p.timer);
  p.layer.remove();
};

// Builds the (hidden) strip layer for `source`.
const build = (source) => {
  const W = source.getBoundingClientRect();
  const ww = W.width;
  const wh = W.height;
  const step = Math.max(STRIP, Math.ceil(wh / MAX_STRIPS));

  // One trimmed copy of the window, shown open whatever state the real one is in.
  const template = source.cloneNode(true);

  // Scrolling areas (the note, the list, the sidebar) only need to show the same part of their
  // content in the copy, not to scroll. Left scrollable, each one in every strip's copy becomes a
  // GPU layer as big as the whole area: hundreds in a large window, enough to run the graphics
  // memory out and leave the screen half-drawn. So they become plain clipped boxes, marked with
  // where they were scrolled to, keeping room for a scrollbar if they had one so nothing moves.
  const originals = source.querySelectorAll("*");
  template.querySelectorAll("*").forEach((el, i) => {
    const original = originals[i];
    const style = getComputedStyle(original);
    if (!/auto|scroll/.test(style.overflowX + style.overflowY)) return;
    el.style.overflow = "hidden";
    const borders = parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
    if (original.offsetWidth - original.clientWidth > borders) el.style.scrollbarGutter = "stable";
    if (original.scrollTop || original.scrollLeft) {
      el.dataset.genieScroll = `${original.scrollTop} ${original.scrollLeft}`;
    }
  });

  // A live page (Chrome's) can't be copied: a copied <iframe> would load the page again in every
  // strip. The copy shows a blank page in its place.
  template.querySelectorAll("iframe").forEach((frame) => {
    const blank = document.createElement("div");
    blank.className = frame.className;
    blank.hidden = frame.hidden;
    frame.replaceWith(blank);
  });

  template.removeAttribute("id");
  template.classList.remove("is-genie-hidden", "nw--minimized", "nw--closed");
  template.classList.add("nw--open");
  // The copy gets its own ids, and its SVG icons point at its own masks: the window's originals
  // are hidden while the copy plays, and a hidden mask hides everything drawn through it.
  const ids = new Map();
  template.querySelectorAll("[id]").forEach((el) => {
    ids.set(el.id, `${el.id}-genie`);
    el.id = ids.get(el.id);
  });
  if (ids.size) {
    template.querySelectorAll("*").forEach((el) => {
      for (const attr of el.attributes) {
        if (attr.value.includes("url(#") || (/(^|:)href$/.test(attr.name) && attr.value.startsWith("#"))) {
          attr.value = attr.value.replace(/#([^)\s"']+)/g, (m, id) => (ids.has(id) ? `#${ids.get(id)}` : m));
        }
      }
    });
  }
  pruneBelow(source, template, W.bottom + 40);

  const layer = document.createElement("div");
  layer.className = "genie-layer";
  layer.setAttribute("aria-hidden", "true");

  // The window's drop shadow, which fades as the window starts to bend.
  const shadow = document.createElement("div");
  shadow.className = "genie-shadow";
  shadow.style.cssText = `left:${W.left}px;top:${W.top}px;width:${ww}px;height:${wh}px`;
  layer.appendChild(shadow);

  const strips = [];
  for (let r0 = 0; r0 < wh; r0 += step) {
    // Each strip carries one extra row overlapping the next, so their soft (anti-aliased) edges
    // never let a hairline of background through where they meet.
    const rows = Math.min(step + 1, wh - r0);
    const strip = document.createElement("div");
    strip.className = "genie-strip";
    // Start exactly over the window (full size), so the browser paints it at full resolution
    // while it's still invisible; playing it later then costs no painting at all.
    strip.style.cssText = `width:${ww}px;height:${rows}px;transform:matrix3d(1,0,0,1e-7,0,1,0,0,0,0,1,0,${W.left},${W.top + r0},0,1)`;
    const holder = document.createElement("div");
    holder.className = "genie-holder";
    holder.style.cssText = `top:${-r0}px;width:${ww}px;height:${wh}px`;
    const copy = template.cloneNode(true);
    holder.appendChild(copy);
    strip.appendChild(holder);
    layer.appendChild(strip);
    strips.push({ strip, copy, r0, rows });
  }
  document.body.appendChild(layer);
  layer.querySelectorAll("[data-genie-scroll]").forEach((el) => {
    const [top, left] = el.dataset.genieScroll.split(" ");
    el.scrollTop = Number(top);
    el.scrollLeft = Number(left);
  });
  layer.getBoundingClientRect(); // lay it all out now, while nothing is animating

  // Any change inside the window (a note selected, the clock text, a folder opened) or to its
  // size makes the copy out of date, so it's thrown away at once rather than kept in memory.
  // Changes to the window element's own attributes (its open/hidden classes) don't count.
  const changed = new MutationObserver((records) => {
    if (records.some((r) => r.target !== source)) discard(source);
  });
  changed.observe(source, { subtree: true, childList: true, characterData: true, attributes: true });
  let initial = true; // a ResizeObserver reports the starting size once; that's not a change
  const resized = new ResizeObserver(() => {
    if (initial) initial = false;
    else discard(source);
  });
  resized.observe(source);
  return { layer, shadow, strips, W, key: snapshotKey(source), observers: [changed, resized] };
};

// Builds the strips for `source` ahead of time, so a later genie() starts instantly.
export const prepareGenie = (source) => {
  if (!source || reducedMotion()) return;
  const existing = prepared.get(source);
  if (existing && existing.key === snapshotKey(source)) {
    clearTimeout(existing.timer);
    existing.timer = setTimeout(() => discard(source), PREPARED_TTL);
    return;
  }
  discard(source);
  const entry = build(source);
  entry.timer = setTimeout(() => discard(source), PREPARED_TTL);
  prepared.set(source, entry);
};

// The animation currently playing for each window, so a new one can cut it short.
const running = new WeakMap();

// Runs the effect from `source` (the window element) into `target` (its Dock icon), or out of it
// when `reverse`. Resolves with a cleanup function that removes the animated copy: call it once
// the real window is showing again (or hidden for good), so there's never a blank frame between.
export const genie = ({ source, target, reverse = false, duration = reverse ? 520 : 600 }) =>
  new Promise((resolve) => {
    // Clicking the Dock icon mid-minimize (or minimizing mid-restore) replaces the running one.
    if (running.has(source)) running.get(source)();

    prepareGenie(source);
    const entry = prepared.get(source);
    prepared.delete(source);
    entry.observers.forEach((o) => o.disconnect());
    clearTimeout(entry.timer);

    const { layer, shadow, strips, W } = entry;
    const I = target.getBoundingClientRect();
    const wx = W.left;
    const wy = W.top;
    const ww = W.width;
    const wh = W.height;
    const ix = I.left;
    const iy = I.top;
    const iw = I.width;
    const ih = I.height;

    // Horizontal span of the funnel at screen height y, for squeeze progress a.
    const span = (y, a) => {
      const f = a * smoothstep((y - wy) / Math.max(1, iy - wy));
      return [wx + (ix - wx) * f, wx + ww + (ix + iw - wx - ww) * f];
    };

    const draw = (p) => {
      const a = easeInOut(clamp01(p / SQUEEZE_END));
      const b = easeIn(clamp01((p - SLIDE_START) / (1 - SLIDE_START)));
      // A window row slides from where it is to its place inside the icon.
      const rowY = (r) => wy + r + (iy + (r / wh) * ih - (wy + r)) * b;
      const fade = p > 0.94 ? String(1 - (p - 0.94) / 0.06) : "1";
      for (const { strip, r0, rows } of strips) {
        const yTop = rowY(r0);
        const yBot = rowY(r0 + rows);
        const [lt, rt] = span(yTop, a);
        const [lb, rb] = span(yBot, a);
        strip.style.transform = quadTransform(ww, rows, [lt, yTop], [rt, yTop], [rb, yBot], [lb, yBot]);
        strip.style.opacity = fade;
      }
      shadow.style.opacity = String(1 - smoothstep(p / 0.3));
    };

    let raf = 0;
    let finished = false;
    const cleanup = () => layer.remove();
    const finish = () => {
      if (finished) return;
      finished = true;
      cancelAnimationFrame(raf);
      if (running.get(source) === cancel) running.delete(source);
      resolve(cleanup);
    };
    // Cut short by a newer animation: stop and remove this copy straight away.
    const cancel = () => {
      finish();
      cleanup();
    };
    running.set(source, cancel);

    draw(reverse ? 1 : 0);
    layer.classList.add("is-playing");
    const start = performance.now();
    const frame = (now) => {
      const t = clamp01((now - start) / duration);
      draw(reverse ? 1 - t : t);
      if (t < 1) raf = requestAnimationFrame(frame);
      else finish();
    };
    raf = requestAnimationFrame(frame);
  });
